import { DrawableImage, SpreadDTO } from '@lite-workspace/dto-fotoclass-book';
import { FC_FaceDTO } from '@lite-workspace/dto-fotoclass-photo';
// ============================================
// Constants
// ============================================

/**
 * Scale factor for face bounding box.
 * Shrinks bbox to 1/3 — only detects when fold hits the center of the face.
 * Matches the shrink logic in CropController.checkFaceOnFold (image-resize-modal).
 */
const FACE_BBOX_SCALE = 1 / 3;

// ============================================
// Types
// ============================================

export interface FaceOnFoldResult {
  /** True if at least one face overlaps the fold line */
  hasFaceOnFold: boolean;
  /** Spread IDs containing photos with faces on fold */
  spreadIds: (number | string)[];
  /** Total count of affected images */
  count: number;
}

interface FaceBbox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface BookPrintSizes {
  width: number;
  height: number;
}

// ============================================
// Main Detection Functions
// ============================================

/**
 * Checks all spreads in the book model for faces on fold.
 * Returns aggregated result with affected spread IDs.
 */
export function checkFaceOnFoldInBook(spreads: SpreadDTO[], printSizes: BookPrintSizes): FaceOnFoldResult {
  const spreadIds: (number | string)[] = [];
  let count = 0;

  for (let i = 0; i < spreads.length; i++) {
    const spread = spreads[i];
    const hasIssue = checkFaceOnFoldInSpread(spread, printSizes);
    if (hasIssue) {
      count++;
      if (spread.id != null) {
        spreadIds.push(spread.id);
      }

      console.warn(
        `[FaceOnFold] Обнаружено лицо на сгибе: разворот #${i + 1}, id=${spread.id ?? 'N/A'}, ` +
          `type=${spread.spread_type}, pageWidth=${printSizes.width / 2}`,
      );
    }
  }

  return {
    hasFaceOnFold: count > 0,
    spreadIds,
    count,
  };
}

/**
 * Checks a single spread for faces on fold.
 */
export function checkFaceOnFoldInSpread(spread: SpreadDTO, printSizes: BookPrintSizes): boolean {
  const images = collectImageDrawablesWithFaces(spread);
  const pageWidth = printSizes.width / 2;

  for (const img of images) {
    const foldLineX = calculateFoldLineInContainerCoords(img, pageWidth);
    if (foldLineX === null) continue;

    const hasFaceOnFold = detectFaceOnFold(img.image?.faces ?? [], img.naturalAligns?.image, foldLineX);

    if (hasFaceOnFold) {
      return true;
    }
  }

  return false;
}

/**
 * Detects if any face in the image overlaps with the fold line.
 *
 * @param faces Array of detected faces with relative bounding boxes
 * @param imageRect Image rectangle in container coordinates
 * @param foldLineX X coordinate of fold line in container coordinates
 * @returns true if any face overlaps the fold line
 */
export function detectFaceOnFold(
  faces: FC_FaceDTO[],
  imageRect: { width?: number; height?: number; left?: number; top?: number } | undefined,
  foldLineX: number,
): boolean {
  if (!faces || faces.length === 0 || !imageRect) {
    return false;
  }

  const imageWidth = imageRect.width ?? 0;
  const imageLeft = imageRect.left ?? 0;

  if (imageWidth === 0) {
    return false;
  }

  // Convert fold line to relative image coordinates (0-1 range)
  const foldPositionOnImage = (foldLineX - imageLeft) / imageWidth;

  // Check if fold is outside image bounds
  if (foldPositionOnImage < 0 || foldPositionOnImage > 1) {
    return false;
  }

  for (const face of faces) {
    const shrunk = shrinkBbox(face.relativeBbox as FaceBbox);

    // Check if fold line intersects with the shrunken face bbox
    if (foldPositionOnImage >= shrunk.x1 && foldPositionOnImage <= shrunk.x2) {
      return true;
    }
  }

  return false;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Calculates the fold line position in container local coordinates.
 *
 * @param drawable Image drawable with container position and collage index
 * @param pageWidth Width of a single page (half of spread width)
 * @returns X coordinate of fold line in container coords, or null if container doesn't cross fold
 */
function calculateFoldLineInContainerCoords(drawable: DrawableImage, pageWidth: number): number | null {
  const container = drawable.naturalAligns?.container;
  if (!container || container.left === undefined || container.width === undefined) {
    return null;
  }

  const collageIndex = drawable.collageIndex ?? 0;

  // Calculate container position in global (spread) coordinates
  const containerGlobalLeft = container.left + (collageIndex === 1 ? pageWidth : 0);
  const containerGlobalRight = containerGlobalLeft + container.width;

  // Check if container crosses the fold line (center of spread)
  if (containerGlobalLeft < pageWidth && containerGlobalRight > pageWidth) {
    // Return fold position relative to container
    return pageWidth - containerGlobalLeft;
  }

  return null;
}

/**
 * Shrinks the face bounding box to approximate the actual face area.
 * Real face typically occupies about 2/3 of the detected bounding box.
 */
function shrinkBbox(bbox: FaceBbox): FaceBbox {
  const width = bbox.x2 - bbox.x1;
  const height = bbox.y2 - bbox.y1;
  const shrinkX = (width * (1 - FACE_BBOX_SCALE)) / 2;
  const shrinkY = (height * (1 - FACE_BBOX_SCALE)) / 2;

  return {
    x1: bbox.x1 + shrinkX,
    y1: bbox.y1 + shrinkY,
    x2: bbox.x2 - shrinkX,
    y2: bbox.y2 - shrinkY,
  };
}

/**
 * Collects image drawables that have faces from a spread.
 */
function collectImageDrawablesWithFaces(spread: SpreadDTO): DrawableImage[] {
  const out: DrawableImage[] = [];

  for (let collageIndex = 0; collageIndex < (spread.collages?.length ?? 0); collageIndex++) {
    const collage = spread.collages[collageIndex];
    for (const d of collage.drawableObjects ?? []) {
      const drawable = d as DrawableImage;
      if (drawable?.type === 'image' && drawable.image?.id && drawable.image?.faces?.length) {
        // Set collage index if not already set
        if (drawable.collageIndex === undefined) {
          drawable.collageIndex = collageIndex;
        }

        out.push(drawable);
      }
    }
  }

  return out;
}
